/**
 * GameOverModal — compact end-game popup.
 *
 * Replaces the old inline GameOverScreen section that sat below the board
 * and forced the user to scroll on mobile. Mobile-first compact layout:
 *
 *   ┌──────────────────────────┐
 *   │  ●  WHITE wins           │
 *   │     by 3 rings · 42 mv   │
 *   │  ───────────────────────│
 *   │  Moves 42 · W–B 3-1     │
 *   │  ───────────────────────│
 *   │  [Close]  [Back to lobby]│
 *   └──────────────────────────┘
 *
 * The host page renders this as an overlay (position: fixed) so it does
 * NOT push the board content. When the user clicks Close the modal hides
 * and a small floating button (rendered by the parent) reopens it.
 */

import type { YinshState } from "@cloudnest/yinsh-engine";
import { useEffect } from "react";
import { Button } from "../../../ui";

interface GameOverModalProps {
  readonly gameState: YinshState;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onLeave: () => void;
  readonly onReview: () => void;
  readonly whiteName: string;
  readonly blackName: string;
}

function TrophyBlock({
  winnerColor,
}: {
  winnerColor: "WHITE" | "BLACK" | null;
}) {
  const bg =
    winnerColor === "WHITE"
      ? "var(--cn-yinsh-piece-white)"
      : winnerColor === "BLACK"
        ? "var(--cn-yinsh-piece-black)"
        : "var(--cn-surface-tertiary)";
  const ringStroke =
    winnerColor === "BLACK"
      ? "var(--cn-yinsh-piece-white)"
      : "var(--cn-yinsh-piece-black)";

  return (
    <div
      data-testid="trophy-block"
      aria-hidden="true"
      style={{
        width: 56,
        height: 56,
        borderRadius: "var(--cn-radius-md)",
        background: bg,
        border: "2px solid var(--cn-yinsh-ring-border)",
        boxShadow: "var(--cn-shadow-sm)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width={32} height={32} viewBox="0 0 56 56" aria-hidden="true">
        <circle
          cx={28}
          cy={28}
          r={20}
          fill="none"
          stroke={ringStroke}
          strokeWidth={6}
        />
      </svg>
    </div>
  );
}

export function GameOverModal({
  gameState,
  open,
  onClose,
  onLeave,
  onReview,
  whiteName,
  blackName,
}: GameOverModalProps) {
  // Esc closes the dialog while it's open — ARIA APG §3.8 for role=dialog.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const { winner, isDraw, turn, board, players } = gameState;
  const [white, black] = players;

  const winnerName =
    winner === "WHITE" ? whiteName : winner === "BLACK" ? blackName : null;

  const headline = isDraw
    ? "무승부"
    : winnerName
      ? `${winnerName} 승리`
      : "게임 종료";
  const subhead = isDraw ? `${turn}수 후 종료` : `링 3개 획득 · ${turn}수`;

  const markersPlayed = Object.values(board).filter(
    (cell) => cell?.type === "MARKER",
  ).length;

  return (
    <div
      data-testid="game-over-overlay"
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "color-mix(in oklab, black 50%, transparent)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 100,
      }}
    >
      <section
        data-testid="game-over"
        role="dialog"
        aria-modal="true"
        aria-labelledby="game-over-headline"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 360,
          padding: 20,
          background: "var(--cn-surface-primary)",
          border: "1px solid var(--cn-border-subtle)",
          borderRadius: "var(--cn-radius-lg)",
          boxShadow: "var(--cn-shadow-md)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            minWidth: 0,
          }}
        >
          <TrophyBlock winnerColor={winner} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              id="game-over-headline"
              data-testid="game-over-headline"
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 600,
                color: "var(--cn-text-primary)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {headline}
            </h2>
            <p
              style={{
                margin: "2px 0 0",
                color: "var(--cn-text-secondary)",
                fontSize: 13,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subhead}
            </p>
          </div>
        </div>

        <div
          data-testid="game-over-stats"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            padding: "10px 12px",
            background: "var(--cn-surface-secondary)",
            border: "1px solid var(--cn-border-subtle)",
            borderRadius: "var(--cn-radius-md)",
            fontSize: 13,
            color: "var(--cn-text-secondary)",
          }}
        >
          <span>
            수 <strong>{turn}</strong>
          </span>
          <span>
            마커 <strong>{markersPlayed}</strong>
          </span>
          <span>
            백–흑{" "}
            <strong>
              {white.ringsScored}–{black.ringsScored}
            </strong>
          </span>
          <span>
            승자 <strong>{winnerName ?? "—"}</strong>
          </span>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <Button
            variant="primary"
            onClick={onReview}
            data-testid="game-over-review"
            className="w-full"
          >
            리플레이 보기
          </Button>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Button
              variant="secondary"
              onClick={onClose}
              data-testid="game-over-close"
            >
              닫기
            </Button>
            <Button
              variant="secondary"
              onClick={onLeave}
              data-testid="game-over-back"
            >
              로비로
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
